import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react';
import configService from '../../../services/config.service';

const SCRIPT_SRC =
  'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

// Load the Cloudflare Turnstile script once and share the promise.
let scriptPromise = null;
const loadTurnstileScript = () => {
  if (typeof window !== 'undefined' && window.turnstile) {
    return Promise.resolve();
  }
  if (scriptPromise) {
    return scriptPromise;
  }
  scriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => {
      scriptPromise = null;
      reject(new Error('Failed to load Turnstile'));
    };
    document.head.appendChild(script);
  });
  return scriptPromise;
};

/**
 * Renders a Cloudflare Turnstile widget.
 *
 * - Emits the token through `onVerify(token)` once the challenge is solved.
 * - Clears the token via `onExpire()` on expiry/error.
 * - Renders nothing (and stays out of the way) when no site key is configured,
 *   so local/dev environments without Turnstile keep working.
 *
 * Exposes an imperative `reset()` so the parent can request a fresh token
 * after a failed submit (Turnstile tokens are single-use).
 */
const TurnstileWidget = forwardRef(({ onVerify, onExpire }, ref) => {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const onVerifyRef = useRef(onVerify);
  const onExpireRef = useRef(onExpire);
  const siteKey = configService.turnstileSiteKey();

  onVerifyRef.current = onVerify;
  onExpireRef.current = onExpire;

  useImperativeHandle(ref, () => ({
    reset() {
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.reset(widgetIdRef.current);
      }
    },
  }));

  useEffect(() => {
    if (!siteKey) {
      return undefined;
    }
    let cancelled = false;

    loadTurnstileScript()
      .then(() => {
        if (cancelled || !containerRef.current || !window.turnstile) {
          return;
        }
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          callback: (token) =>
            onVerifyRef.current && onVerifyRef.current(token),
          'expired-callback': () =>
            onExpireRef.current && onExpireRef.current(),
          'error-callback': () => onExpireRef.current && onExpireRef.current(),
        });
      })
      .catch(() => {
        /* Script failed to load; submission fallback is handled by the parent. */
      });

    return () => {
      cancelled = true;
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          /* widget already gone */
        }
        widgetIdRef.current = null;
      }
    };
  }, [siteKey]);

  if (!siteKey) {
    return null;
  }

  return <div ref={containerRef} className="mt-4 flex justify-center" />;
});

TurnstileWidget.displayName = 'TurnstileWidget';

export default TurnstileWidget;
